<app-modal-container
  *ngIf="formGroup"
  data-qa="global-ai-chat-container"
  [title]="AiStrings.buttons.default"
  [buttons]="{close: true}"
  [bigSize]="true"
  (closeButtonClicked)="closeModal()">
  <div [formGroup]="formGroup" class="w-[80rem] max-h-[51rem] flex">
    <div class="flex flex-grow flex-col w-1/2 min-h-[40rem] gap-1.25 items-center self-stretch pt-1.25">
      <!-- messages + default requests container -->
      <div class="flex flex-col flex-grow justify-end green-scroll overflow-y-auto w-full">
        <div
          class="flex-grow-0 overflow-auto"
          appAutoScroll
          [autoScrollReset]="autoScrollReset$">

          @if (chat.messages.length) {
            <!-- messages -->
            <div class="flex flex-col items-stretch gap-1.25">
              @for (message of chat.messages; track $index) {
                <!-- user message -->
                @if (message.role === MessageType.USER) {
                  <div class="pl-4.375 flex gap-1.25 justify-start self-stretch items-start">
                    <div
                      [attr.data-qa]="'global-ai-chat-user-message-' + $index"
                      class="p-1.25 bg-black-2 rounded-md flex flex-grow font-3">
                      {{ message.text }}
                    </div>
                    <app-avatar-icon data-qa="user-avatar" class="w-1.875 h-1.875 overflow-hidden rounded-md shrink-0"/>
                  </div>
                }

                <!-- assistant message -->
                @if (message.role === MessageType.ASSISTANT) {
                  <div class="pr-4.375 flex gap-1.25 justify-start self-stretch items-start max-w-full">
                    <div class="flex items-center justify-center">
                      <app-all-icons data-qa="orca-logo" [iconNameAndStyling]="'orca-logo-circle.svg; icon-size-1p875'"/>
                    </div>
                    <div class="flex flex-row gap-1.25 flex-grow max-w-full">
                      <div class="w-full">
                        <markdown
                          data-qa="global-ai-chat-response-markdown"
                          class="markdown max-w-[30rem]"
                          [class.thinking-message]="message.isThinking"
                          [data]="message.text">
                        </markdown>
                      </div>
                    </div>
                  </div>
                }
              }
            </div>
          }
        </div>

        <!-- default requests container -->
        @if (!chat.messages.length) {
          <div class="flex gap-0.9375 items-end">
            @for (action of defaultActions; track $index) {
              <div
                [attr.data-qa]="'global-ai-chat-action-' + $index"
                class="ai-action-card font-4 black-9"
                (click)="sendRequest(action)">
                <span>{{action}}</span>
              </div>
            }
          </div>
        }
      </div>
      <div class="flex flex-col gap-0.625 w-full">
        <!-- request input container -->
        <div [attr.data-qa]="'has-active-request:'+globalAiService.hasActiveRequest" class="relative w-full rounded-[1.25rem] overflow-hidden">
          <app-input
            data-qa="global-ai-chat-request-input"
            formControlName="requestInput"
            class="input-with-generate-button green-scroll"
            textArea="true"
            placeholder="{{AiStrings.placeholders.ask}}"
            (keyup.enter)="sendRequest(formGroup.controls.requestInput.getRawValue())"
          />
          <app-ai-button
            data-qa="global-ai-chat-generate-button"
            class="absolute top-[0.9375rem] right-[0.9375rem]"
            [disabled]="!formGroup.controls.requestInput.valid || globalAiService.hasActiveRequest"
            (buttonClick)="sendRequest(formGroup.controls.requestInput.getRawValue())">
            Generate
          </app-ai-button>

          <div class="selectors-container">

            <!-- SOURCE DATA SELECTOR -->
            <app-selector
              data-qa="data-source-selector"
              formControlName="dataSource"
              class="flex flex-grow selector-title"
              textClass="font-4"
              title="Source data"
              [outerClass]="'control-shrinked-container'"
              [data]="dataSources"
            />

            <!-- COMPANY SELECTOR -->
            <app-selector
              *ngIf="formGroup.controls.dataSource.getRawValue() === DataSourceType.COMPANY"
              data-qa="company-selector"
              formControlName="company"
              #companySelector
              class="flex flex-grow selector-title"
              title="Company"
              textClass="font-4"
              [outerClass]="'control-shrinked-container'"
              [data]="companies"
            />

            <!-- FUND SELECTOR -->
            <app-selector
              *ngIf="formGroup.controls.dataSource.getRawValue() === DataSourceType.FUND"
              data-qa="fund-selector"
              formControlName="fund"
              #fundSelector
              class="flex flex-grow selector-title"
              title="Fund"
              textClass="font-4"
              [outerClass]="'control-shrinked-container'"
              [data]="funds"
            />

            <!-- FUNDS CHECKBOX SELECTOR -->
            <app-checkbox-selector
              *ngIf="formGroup.controls.dataSource.getRawValue() === DataSourceType.ORGANIZATION || formGroup.controls.dataSource.getRawValue() === DataSourceType.COMPANY"
              data-qa="funds-checkbox-selector"
              class="flex flex-grow cursor-pointer selector-title items-end"
              isShrinked="true"
              [title]="'Funds'"
              [props]="checkboxSelectorProps"
              [items]="formGroup.controls.funds.getRawValue()"
              [customText]="selectedFundsCount + ' of ' + formGroup.controls.funds.getRawValue().length + ' selected'"
              [blockRepositioning]="false"
              (itemEmitter)="onFundsSelectionChange()"
            />

            <!-- PERIOD SELECTOR FOR ORG -->
            <app-input-date
              *ngIf="formGroup.controls.dataSource.getRawValue() === DataSourceType.ORGANIZATION"
              data-qa="org-period-selector"
              class="flex flex-grow selector-title items-end"
              formControlName="period"
              title="Period"
              [isShrinked]="true"
              [fontClass]="'font-4'"
            />

            <!-- PERIOD SELECTOR FOR FUNDS AND COMPANIES -->
            <app-selector
              *ngIf="formGroup.controls.dataSource.getRawValue() === DataSourceType.FUND || formGroup.controls.dataSource.getRawValue() === DataSourceType.COMPANY"
              data-qa="fund-or-company-period-selector"
              formControlName="period"
              class="flex flex-grow selector-title"
              textClass="font-4"
              title="Period"
              [outerClass]="'control-shrinked-container'"
              [data]="periods"
            />
          </div>
        </div>
        <div class="w-full text-right caption-2 bold500 black-10 pt-0.625">
          History contains your last 5 messages.
          <span
            data-qa="clear-chat"
            class="underline cursor-pointer"
            (click)="clearChat()">
          Clear the chat
        </span>
        </div>
        <div
          *ngIf="!isProd"
          data-qa="debug-details-toggle"
          class="w-full mt-0.625 flex flex-col items-center">
          <div class="flex items-center" (click)="toggleDebugDetails()">
            <span class="caption-2 bold500">{{ isDebugExpanded ? 'Hide details' : 'Show details' }}</span>
            <app-table-group-icon [opened]="isDebugExpanded"/>
          </div>
          <div
            *ngIf="isDebugExpanded"
            class="max-w-full mt-0.625 p-0.625 bg-black-2 rounded-md">
            <p class="font-3">This section shows the data and prompts that were/will be provided to llm based on the data from the selectors.</p>

            <!-- Debugging chunks display -->
            <div *ngIf="debuggingChunks" class="mt-0.625 flex max-h-[27rem] flex-col overflow-scroll gap-0.625">
              <h4 class="font-3 bold600">Debugging Information:</h4>
              <div *ngFor="let chunk of [debuggingChunks.data, debuggingChunks.prompt]" class="flex">
                <markdown class="markdown max-w-[25rem]" [data]="chunk"></markdown>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    @if (chat.charts.length) {
      <div
        data-qa="charts-container"
        class="flex flex-grow flex-col w-1/2 gap-1.25 self-stretch pt-1.25 pb-1.875 px-[1rem]">
        <div class="overflow-auto">
          <div class="flex flex-col flex-grow justify-end">
            <div
              class="flex-grow-0"
              appAutoScroll
              [autoScrollReset]="autoScrollReset$">
              @for (chart of chat.charts; track chart) {
                @if (chart.options && chart.chartId) {
                  <app-base-chart
                    [chartId]="chart.chartId"
                    [options]="chart.options"
                    @fadeInOut/>
                }
              }
            </div>
          </div>
        </div>
      </div>
    } @else {
      <div data-qa="no-charts-container" class="flex w-1/2">
        <div class="w-full flex justify-center items-center relative">
          <img class="h-[30rem]" src="/assets/webp/ai-charts.webp" alt="AI charts">
          <div class="absolute top-[58%] flex flex-col justify-center items-center">
            <span class="font-2 bold600">{{AiStrings.placeholders.charts}}</span>
            <span class="font-3 black-9">{{AiStrings.placeholders.canVisualize}}</span>
          </div>
        </div>
      </div>
    }
  </div>
</app-modal-container>
